/**
 * AnalyticsPage — KPI dashboard + activity-per-participant table.
 *
 * Data is fetched from existing endpoints and aggregated frontend-side;
 * no dedicated /analytics endpoint is required.
 *
 * KPI notes:
 *  - Deltagare  : all participants regardless of period
 *  - Mål        : goals with status='active'
 *  - Närvaro%   : attendance records in period (present / total)
 *  - OCN        : ocn-validations in period
 *  - Möten      : meeting-notes in period
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Users, TrendingUp, Calendar, Award, FileText, RotateCcw, Briefcase, GraduationCap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import api from '../lib/api';
import { toast } from 'sonner';
import KpiCard from './KpiCard';
import ActivityTable from './ActivityTable';

// M6 — etiketter för outcomes-aggregat
const OUTCOME_CATEGORY_LABELS = {
  arbete: 'Övergång till arbete',
  studier: 'Övergång till studier',
  annan_insats: 'Annan insats',
  fullfoljt: 'Fullföljd process',
  avbrott: 'Avbrott',
  ingen_overgang: 'Ingen tydlig övergång',
};

const EMPLOYMENT_TYPE_LABELS = {
  tillsvidare: 'Tillsvidare',
  visstid: 'Visstid',
  vikariat: 'Vikariat',
  provanstallning: 'Provanställning',
  egen_foretagare: 'Egen företagare',
  annat: 'Annat',
};

const EMPLOYMENT_SCOPE_LABELS = {
  heltid: 'Heltid',
  deltid_75: 'Deltid 75 %',
  deltid_50: 'Deltid 50 %',
  deltid_25: 'Deltid 25 %',
  timanstalld: 'Timanställd',
};

const EDUCATION_TYPE_LABELS = {
  gymnasium: 'Gymnasium',
  komvux: 'Komvux',
  yrkesutbildning: 'Yrkesutbildning',
  hogskola: 'Högskola',
  folkbildning: 'Folkbildning',
  sfi: 'SFI',
  annat: 'Annat',
};

// ── Period helpers ─────────────────────────────────────────────────────────────

const today = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const PERIODS = [
  { value: '7d',   label: 'Senaste 7 dagarna'  },
  { value: '30d',  label: 'Senaste 30 dagarna'  },
  { value: '90d',  label: 'Senaste 90 dagarna'  },
  { value: 'year', label: 'Detta år'            },
  { value: 'custom', label: 'Anpassat'          },
];

export const getPeriodRange = (period, customFrom, customTo) => {
  const now = today();
  switch (period) {
    case '7d':    return { from: daysAgo(7),   to: now };
    case '30d':   return { from: daysAgo(30),  to: now };
    case '90d':   return { from: daysAgo(90),  to: now };
    case 'year':  return { from: new Date(new Date().getFullYear(), 0, 1), to: now };
    case 'custom':
      return {
        from: customFrom ? new Date(customFrom) : null,
        to:   customTo   ? new Date(customTo)   : null,
      };
    default:      return { from: daysAgo(30), to: now };
  }
};

const parseDate = (str) => {
  if (!str) return null;
  return new Date(str);
};

const inRange = (dateStr, from, to) => {
  if (!dateStr) return false;
  const d = parseDate(dateStr);
  if (!d) return false;
  if (from && d < from) return false;
  if (to   && d > to)   return false;
  return true;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return d.toLocaleDateString('sv-SE');
};

const DEFAULT_PERIOD    = '30d';
const DEFAULT_CLIENT_ID = '';

// ── Component ──────────────────────────────────────────────────────────────────

const AnalyticsPage = () => {
  useEffect(() => {
    api.post?.('/org/onboarding/user-event', { event_key: 'analytics_opened' })?.catch(() => {});
  }, []);

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [period, setPeriod]         = useState(DEFAULT_PERIOD);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo]     = useState('');
  const [clientId, setClientId]     = useState(DEFAULT_CLIENT_ID);

  // ── Raw data ─────────────────────────────────────────────────────────────────
  const [participants, setParticipants]   = useState([]);
  const [goals, setGoals]                 = useState([]);
  const [attendance, setAttendance]       = useState([]);
  const [meetingNotes, setMeetingNotes]   = useState([]);
  const [ocnValidations, setOcnValidations] = useState([]);
  const [clients, setClients]             = useState([]);
  const [outcomes, setOutcomes]           = useState(null); // M6
  const [followupStats, setFollowupStats] = useState(null); // N5
  const [loading, setLoading]             = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        api.get('/participants'),
        api.get('/goals'),
        api.get('/attendance'),
        api.get('/meeting-notes'),
        api.get('/ocn-validations'),
        api.get('/clients'),
        api.get('/analytics/outcomes'),
        api.get('/analytics/outcome-followup-stats'),
      ]);
      const safe = (r, fallback = []) => r.status === 'fulfilled' ? (r.value?.data || fallback) : fallback;
      setParticipants(safe(results[0]));
      setGoals(safe(results[1]));
      setAttendance(safe(results[2]));
      setMeetingNotes(safe(results[3]));
      setOcnValidations(safe(results[4]));
      setClients(safe(results[5]));
      setOutcomes(results[6].status === 'fulfilled' ? results[6].value.data : null);
      setFollowupStats(results[7].status === 'fulfilled' ? results[7].value.data : null);
    } catch {
      toast.error('Kunde inte hämta analysdata');
    } finally {
      setLoading(false);
    }
  };

  // ── Filter helpers ────────────────────────────────────────────────────────────

  const { from, to } = useMemo(
    () => getPeriodRange(period, customFrom, customTo),
    [period, customFrom, customTo]
  );

  const isFiltered = period !== DEFAULT_PERIOD || clientId !== DEFAULT_CLIENT_ID;

  const resetFilters = () => {
    setPeriod(DEFAULT_PERIOD);
    setCustomFrom('');
    setCustomTo('');
    setClientId(DEFAULT_CLIENT_ID);
  };

  // Filter participants by selected client
  const filteredParticipants = useMemo(() =>
    clientId
      ? participants.filter(p => p.client_id === clientId)
      : participants,
  [participants, clientId]);

  const filteredPids = useMemo(
    () => new Set(filteredParticipants.map(p => p.id)),
    [filteredParticipants]
  );

  // ── KPI calculations ──────────────────────────────────────────────────────────

  const kpis = useMemo(() => {
    // Attendance in period for filtered participants
    const attendanceInPeriod = attendance.filter(r =>
      filteredPids.has(r.participant_id) &&
      inRange(r.date, from, to)
    );
    const presentCount = attendanceInPeriod.filter(r => r.status === 'present').length;
    const attendancePct = attendanceInPeriod.length > 0
      ? Math.round((presentCount / attendanceInPeriod.length) * 100)
      : null;

    // Meeting notes in period
    const meetingsInPeriod = meetingNotes.filter(r =>
      filteredPids.has(r.participant_id) &&
      inRange(r.motesdatum || r.created_at, from, to)
    );

    // OCN in period
    const ocnInPeriod = ocnValidations.filter(r =>
      filteredPids.has(r.participant_id) &&
      inRange(r.created_at || r.validation_date, from, to)
    );

    // Active goals for filtered participants
    const activeGoals = goals.filter(g =>
      filteredPids.has(g.participant_id) && g.status === 'active'
    );

    return {
      participants:  filteredParticipants.length || null,
      goals:         activeGoals.length      > 0  ? activeGoals.length      : null,
      attendancePct: attendancePct,
      ocn:           ocnInPeriod.length      > 0  ? ocnInPeriod.length      : null,
      meetings:      meetingsInPeriod.length > 0  ? meetingsInPeriod.length : null,
    };
  }, [filteredParticipants, filteredPids, attendance, goals, meetingNotes, ocnValidations, from, to]);

  // ── Activity table rows ───────────────────────────────────────────────────────

  const activityRows = useMemo(() => {
    return filteredParticipants.map(p => {
      const pAttendance = attendance.filter(r =>
        r.participant_id === p.id && inRange(r.date, from, to)
      );
      const presentAtt = pAttendance.filter(r => r.status === 'present').length;
      const attendancePct = pAttendance.length > 0
        ? Math.round((presentAtt / pAttendance.length) * 100)
        : null;

      const pGoals = goals.filter(g => g.participant_id === p.id && g.status === 'active');

      const pMeetings = meetingNotes.filter(r =>
        r.participant_id === p.id && inRange(r.motesdatum || r.created_at, from, to)
      );

      // Last activity: latest of attendance or meeting date in period
      const allDates = [
        ...pAttendance.map(r => r.date),
        ...pMeetings.map(r => r.motesdatum || r.created_at),
      ].filter(Boolean).sort().reverse();
      const lastActivity = allDates[0] ? fmtDate(allDates[0]) : null;

      const status = p.status === 'active' ? 'active'
        : p.status === 'inactive' ? 'inactive'
        : 'unknown';

      return {
        id:            p.id,
        name:          p.name,
        attendancePct,
        goalsCount:    pGoals.length,
        meetingsCount: pMeetings.length,
        lastActivity,
        status,
      };
    });
  }, [filteredParticipants, attendance, goals, meetingNotes, from, to]);

  // ── Select styles ─────────────────────────────────────────────────────────────

  const selectCls = 'h-9 px-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring';

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-page">

        {/* Header */}
        <div>
          <h1 className="text-4xl font-heading font-black tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Översikt över verksamhetens nyckeltal och aktivitet
          </p>
        </div>

        {/* Filter row */}
        <div
          className="flex flex-wrap items-end gap-3 p-4 bg-white border rounded-lg"
          data-testid="filter-row"
        >
          {/* Period */}
          <div className="space-y-1">
            <label htmlFor="filter-period" className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
              Period
            </label>
            <select
              id="filter-period"
              data-testid="filter-period"
              value={period}
              onChange={e => { setPeriod(e.target.value); }}
              className={selectCls}
            >
              {PERIODS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Custom date range */}
          {period === 'custom' && (
            <>
              <div className="space-y-1">
                <label htmlFor="custom-from" className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                  Från
                </label>
                <Input
                  id="custom-from"
                  data-testid="custom-from"
                  type="date"
                  value={customFrom}
                  onChange={e => setCustomFrom(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="custom-to" className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                  Till
                </label>
                <Input
                  id="custom-to"
                  data-testid="custom-to"
                  type="date"
                  value={customTo}
                  onChange={e => setCustomTo(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </>
          )}

          {/* Kund */}
          <div className="space-y-1">
            <label htmlFor="filter-client" className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
              Kund
            </label>
            <select
              id="filter-client"
              data-testid="filter-client"
              value={clientId}
              onChange={e => setClientId(e.target.value)}
              className={selectCls}
            >
              <option value="">Alla kunder</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.namn}</option>
              ))}
            </select>
          </div>

          {/* Reset */}
          {isFiltered && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="reset-filters"
              onClick={resetFilters}
              className="h-9 rounded-full text-xs font-bold gap-1.5 self-end"
            >
              <RotateCcw size={13} />
              Återställ filter
            </Button>
          )}
        </div>

        {/* KPI Grid */}
        <div
          className="grid grid-cols-2 md:grid-cols-3 gap-4"
          data-testid="kpi-grid"
        >
          <KpiCard
            id="participants"
            icon={<Users size={20} />}
            label="Deltagare"
            value={loading ? undefined : kpis.participants}
            loading={loading}
          />
          <KpiCard
            id="goals"
            icon={<TrendingUp size={20} />}
            label="Mål"
            value={loading ? undefined : kpis.goals}
            loading={loading}
          />
          <KpiCard
            id="attendance"
            icon={<Calendar size={20} />}
            label="Närvaro%"
            value={loading ? undefined : kpis.attendancePct}
            unit="%"
            loading={loading}
          />
          <KpiCard
            id="ocn"
            icon={<Award size={20} />}
            label="OCN-valideringar"
            value={loading ? undefined : kpis.ocn}
            loading={loading}
          />
          <KpiCard
            id="meetings"
            icon={<FileText size={20} />}
            label="Mötesanteckningar"
            value={loading ? undefined : kpis.meetings}
            loading={loading}
          />
        </div>

        {/* M6 — Utfall vid avslut */}
        {outcomes && outcomes.total_closed > 0 && (
          <div className="space-y-3" data-testid="outcomes-section">
            <h2
              className="text-xl font-heading font-bold"
              style={{ color: '#333333' }}
            >
              Utfall vid avslut
            </h2>
            <p className="text-sm text-muted-foreground">
              {outcomes.total_with_outcome} av {outcomes.total_closed} avslutade deltagare har registrerat utfall.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-5 space-y-3">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">
                    Fördelning per kategori
                  </p>
                  {Object.keys(outcomes.by_category).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Ingen data ännu.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {Object.entries(outcomes.by_category)
                        .sort(([, a], [, b]) => b - a)
                        .map(([cat, count]) => (
                          <li key={cat} className="flex items-center justify-between text-sm">
                            <span>{OUTCOME_CATEGORY_LABELS[cat] || cat}</span>
                            <span className="font-bold tabular-nums">{count}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 space-y-3">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <Briefcase size={13} /> Arbete — anställningstyp/omfattning
                  </p>
                  {Object.keys(outcomes.employment.by_type).length === 0 && Object.keys(outcomes.employment.by_scope).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Inga arbetsutfall registrerade.</p>
                  ) : (
                    <>
                      {Object.keys(outcomes.employment.by_type).length > 0 && (
                        <ul className="space-y-1 text-sm">
                          {Object.entries(outcomes.employment.by_type).map(([k, v]) => (
                            <li key={k} className="flex items-center justify-between">
                              <span>{EMPLOYMENT_TYPE_LABELS[k] || k}</span>
                              <span className="font-bold tabular-nums">{v}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {Object.keys(outcomes.employment.by_scope).length > 0 && (
                        <ul className="space-y-1 text-sm pt-2 border-t">
                          {Object.entries(outcomes.employment.by_scope).map(([k, v]) => (
                            <li key={k} className="flex items-center justify-between">
                              <span className="text-muted-foreground">{EMPLOYMENT_SCOPE_LABELS[k] || k}</span>
                              <span className="font-bold tabular-nums">{v}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-5 space-y-3">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                    <GraduationCap size={13} /> Studier — utbildningstyp
                  </p>
                  {Object.keys(outcomes.education.by_type).length === 0 ? (
                    <p className="text-sm text-muted-foreground">Inga studieutfall registrerade.</p>
                  ) : (
                    <ul className="space-y-1 text-sm">
                      {Object.entries(outcomes.education.by_type).map(([k, v]) => (
                        <li key={k} className="flex items-center justify-between">
                          <span>{EDUCATION_TYPE_LABELS[k] || k}</span>
                          <span className="font-bold tabular-nums">{v}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* N5 — Uppföljningsresultat (retention) */}
        {followupStats && followupStats.total_followups > 0 && (
          <div className="space-y-3" data-testid="followup-stats-section">
            <h2 className="text-xl font-heading font-bold" style={{ color: '#333333' }}>
              Uppföljningsresultat
            </h2>
            <p className="text-sm text-muted-foreground">
              Andel avslutade deltagare som kvarstår i arbete/studier efter angiven tid.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['30_days', '90_days', '180_days'].map((ftype) => {
                const bucket = followupStats.by_type[ftype] || { completed: 0 };
                const rate = followupStats.retention_rates[ftype];
                const label = ftype === '30_days' ? '30-dagarsretention'
                  : ftype === '90_days' ? '90-dagarsretention'
                  : '180-dagarsretention';
                return (
                  <Card key={ftype} data-testid={`retention-card-${ftype}`}>
                    <CardContent className="p-5 space-y-2">
                      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{label}</p>
                      {bucket.completed < 3 ? (
                        <p className="text-sm text-muted-foreground italic">Otillräckligt underlag</p>
                      ) : (
                        <>
                          <p className="text-3xl font-heading font-black tabular-nums">{rate?.toFixed(1)}%</p>
                          <p className="text-xs text-muted-foreground">
                            {bucket.kvarstår} av {bucket.completed} kvarstår
                          </p>
                        </>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Activity table */}
        <div className="space-y-3">
          <h2
            className="text-xl font-heading font-bold"
            style={{ color: '#333333' }}
            data-testid="activity-section-heading"
          >
            Aktivitet per deltagare
          </h2>
          <ActivityTable rows={activityRows} loading={loading} />
        </div>

      </div>
    </DashboardLayout>
  );
};

export default AnalyticsPage;
